import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CAvatar,
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CButtonGroup,
  CBadge,
  CCol,
  CRow,
  CWidgetStatsB,
  CContainer,
} from "@coreui/react";
import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import CIcon from "@coreui/icons-react";
import { cilFactory } from "@coreui/icons";

const ENTITY_COLORS = {
  ANI: ["#e95b6c", "#a46698"],
  IAPMEI: ["#3b82f6", "#06b6d4"],
  OUTRO: ["#9ca3af", "#d1d5db"],
};

const getProjectPillBackground = (project) => {
  const [color1, color2] =
    ENTITY_COLORS[project.management.responsible_entity] || ENTITY_COLORS.OUTRO;

  if (project.state === "THEORETICAL") {
    return `repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 2px, transparent 2px, transparent 8px), linear-gradient(101deg, ${color1} 0%, ${color2} 100%)`;
  }

  return `linear-gradient(101deg, ${color1} 0%, ${color2} 100%)`;
};

const getProjectPillBorder = (project) =>
  project.management?.no_incentive ? "2px solid #6e6a6a" : "none";

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filterType, setFilterType] = useState("withProjects");

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get("/projects/management/")
      .then((response) => {
        setClients(response.data);
        setFilteredClients(
          response.data.filter((c) => c.projects && c.projects.length > 0),
        );
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleFilter = (type) => {
    setFilterType(type);
    if (type === "withProjects") {
      setFilteredClients(
        clients.filter((c) => c.projects && c.projects.length > 0),
      );
    } else if (type === "withoutProjects") {
      setFilteredClients(
        clients.filter((c) => !c.projects || c.projects.length === 0),
      );
    } else {
      setFilteredClients(clients);
    }
  };

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "";
    const initials = parts.slice(0, 2).map((part) => part[0].toUpperCase());
    return initials.join("");
  };

  const totalClientsWithProjects = clients.filter(
    (c) => c.projects && c.projects.length > 0,
  ).length;
  const totalProjects = clients.reduce(
    (acc, c) => acc + (c.projects ? c.projects.length : 0),
    0,
  );

  const columns = [
    {
      name: "",
      cell: (row) => (
        <CAvatar
          src={row.image || undefined}
          size="lg"
          className="border"
          style={{
            width: "48px",
            height: "48px",
            background: row.image ? "transparent" : "#eef1f5",
            color: "#495057",
            fontWeight: 700,
          }}
        >
          {row.image
            ? null
            : getInitials(row.name) || <CIcon icon={cilFactory} />}
        </CAvatar>
      ),
      sortable: false,
      width: "84px",
      center: true,
      style: {
        padding: "0.5rem",
      },
    },
    {
      name: "Cliente",
      selector: (row) => row.name,
      sortable: true,
      width: "30%",
      wrap: false,
      style: {
        fontWeight: "600",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
      },
    },
    {
      name: "Projetos",
      selector: (row) => (
        <div className="d-flex flex-wrap justify-content-center align-items-center gap-1">
          {row.projects && row.projects.length > 0 ? (
            row.projects.map((project) => (
              <CBadge
                shape="rounded-pill"
                className="text-white px-2 py-2"
                key={project.id}
                style={{
                  background: getProjectPillBackground(project),
                  border: getProjectPillBorder(project),
                  fontSize: "small",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/projetos/gestao/${row.id}?projeto=${project.id}`);
                }}
              >
                {project.name}
              </CBadge>
            ))
          ) : (
            <CBadge
              color="warning"
              shape="rounded-pill"
              className="text-muted"
              style={{ fontSize: "small" }}
            >
              Sem projetos
            </CBadge>
          )}
        </div>
      ),
      className: "text-center",
      width: "60%",
    },
  ];

  return (
    <CContainer>
      <CRow className="g-3 mb-4 align-items-stretch">
        <CCol sm={6} lg={6}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-total"
            value={totalClientsWithProjects}
            title="Clientes com projetos"
            text="Clientes com pelo menos um projeto"
          />
        </CCol>
        <CCol sm={6} lg={6}>
          <CWidgetStatsB
            className="resources-kpi h-100 kpi-completion"
            value={totalProjects}
            title="Projetos"
            text="Total de projetos"
          />
        </CCol>
      </CRow>

      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0">Projetos em Gestão</h3>
          </div>

          <CButtonGroup role="group" size="sm" className="gap-1">
            <CButton
              variant={filterType !== "all" ? "outline" : null}
              color={filterType === "all" ? "primary" : "secondary"}
              onClick={() => handleFilter("all")}
            >
              Todos
            </CButton>
            <CButton
              variant={filterType !== "withProjects" ? "outline" : null}
              color={filterType === "withProjects" ? "primary" : "secondary"}
              onClick={() => handleFilter("withProjects")}
            >
              Com Projetos
            </CButton>
            <CButton
              variant={filterType !== "withoutProjects" ? "outline" : null}
              color={filterType === "withoutProjects" ? "primary" : "secondary"}
              onClick={() => handleFilter("withoutProjects")}
            >
              Sem Projetos
            </CButton>
          </CButtonGroup>
        </CCardHeader>
        <CCardBody>
          <DataTableComponent
            columns={columns}
            data={filteredClients}
            loading={loading}
            error={error}
            onRowClicked={(row) => navigate(`/projetos/gestao/${row.id}`)}
          />
        </CCardBody>
      </CCard>
    </CContainer>
  );
};

export default ClientsList;
